import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { errorMessage } from '../../../utils/apiResponse';
import { money } from '../../../utils/nurseryMoney';
import { useOrgTimezone, formatDateTimeInTz } from '../../../utils/timezone';
import type { AccountHoldReason, OrderAccountHold } from '../../../types/nurseryAccounts';

/**
 * Where an order stands by the organisation's account rules
 * (/accounts/account-rules): held at despatch, refused at confirmation, a
 * warning, or released. The server works every reason out; this words it,
 * and lets whoever the rules allow release the order with a note.
 */
export function AccountHoldCard({ orderId, hold, editable, onChange }: {
  orderId: number;
  hold: OrderAccountHold;
  /** The order has not gone or been cancelled. */
  editable: boolean;
  onChange: (hold: OrderAccountHold) => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'AccountHoldCard' });
  const tz = useOrgTimezone();
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const payer = hold.payer_name || t('thisCustomer');
  const blocking = hold.reasons.filter((r) => r.stops.length > 0);
  const stopped = hold.held || hold.refused_at_confirm;
  if (!hold.reasons.length && !hold.released) return null;

  const say = (r: AccountHoldReason): string => {
    switch (r.code) {
      case 'PREPAY': return t(r.satisfied ? 'prepayPaid' : 'prepay', { payer });
      case 'PREPAY_NO_ABN': return t(r.satisfied ? 'prepayNoAbnPaid' : 'prepayNoAbn', { payer });
      case 'NO_ABN': return t('noAbn', { payer });
      case 'OVER_LIMIT': return t(r.limit_is_default ? 'overDefaultLimit' : 'overLimit', {
        payer, balance: money(r.balance ?? 0), exposure: money(r.exposure ?? 0), limit: money(r.credit_limit ?? 0),
      });
      case 'OVERDUE': return t('overdue', { payer, amount: money(r.overdue ?? 0), days: hold.overdue_days });
      default: return r.message;
    }
  };

  const release = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.releaseAccountHold(orderId, note.trim());
      if (res.success && res.data) { onChange(res.data); setOpen(false); setNote(''); }
      else setError(errorMessage(res, t('releaseFailed')));
    } catch (e) {
      setError(errorMessage(e, t('releaseFailed')));
    }
    setBusy(false);
  };

  const undo = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.undoAccountHoldRelease(orderId);
      if (res.success && res.data) onChange(res.data);
      else setError(errorMessage(res, t('undoFailed')));
    } catch (e) {
      setError(errorMessage(e, t('undoFailed')));
    }
    setBusy(false);
  };

  const tone = stopped
    ? 'bg-red-50 border-red-200 text-red-900'
    : hold.released ? 'bg-green-50 border-green-200 text-green-900' : 'bg-amber-50 border-amber-200 text-amber-900';
  const Icon = hold.released && !stopped ? ShieldCheck : ShieldAlert;

  return (
    <div className={`border rounded-lg px-4 py-3 text-sm break-words space-y-2 ${tone}`}>
      <div className="flex items-start gap-2">
        <Icon className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="min-w-0 space-y-1">
          {hold.held && <p className="font-semibold">{t('held')}</p>}
          {hold.refused_at_confirm && !hold.held && <p className="font-semibold">{t('refusedAtConfirm')}</p>}
          {hold.refused_at_confirm && hold.held && <p>{t('alsoRefused')}</p>}
          {hold.reasons.map((r) => <p key={r.code}>{say(r)}</p>)}
          {!stopped && !hold.released && <p className="text-xs">{t('warningOnly')}</p>}
          {hold.released && (
            <p>
              {t('released', {
                by: hold.released.by_name || t('someone'),
                at: formatDateTimeInTz(hold.released.at, tz),
                note: hold.released.note,
              })}
            </p>
          )}
        </div>
      </div>

      {error && <p className="text-red-700">{error}</p>}

      {editable && hold.can_release && stopped && !open && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>{t('release')}</Button>
        </div>
      )}
      {editable && stopped && !hold.can_release && blocking.length > 0 && (
        <p className="text-xs">{t('whoReleases')}</p>
      )}
      {open && (
        <div className="space-y-2">
          <label htmlFor="account-hold-note" className="block text-sm font-medium">{t('noteLabel')}</label>
          <textarea
            id="account-hold-note"
            value={note}
            rows={2}
            maxLength={1000}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('notePlaceholder')}
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-900"
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={release} disabled={busy || note.trim().length < 5}>{t('releaseConfirm')}</Button>
            <Button size="sm" variant="outline" onClick={() => { setOpen(false); setError(null); }} disabled={busy}>
              {t('cancel')}
            </Button>
          </div>
        </div>
      )}
      {editable && hold.released && hold.can_release && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={undo} disabled={busy}>{t('undo')}</Button>
        </div>
      )}
    </div>
  );
}
